import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  viewStory,
  reactToStory,
  replyToStory,
  deleteStory,
  getStoryViewers,
  getStoryReactions,
  getStoryReplies,
  repostStory
} from "../../../functions/story";
import Moment from "react-moment";

const reactions = [
  { name: "like", emoji: "👍" },
  { name: "love", emoji: "❤️" },
  { name: "haha", emoji: "😂" },
  { name: "wow", emoji: "😮" },
  { name: "sad", emoji: "😢" },
  { name: "angry", emoji: "😠" },
];

const getReactionEmoji = (name) => {
  const r = reactions.find(r => r.name === name);
  return r ? r.emoji : "👍";
};

export default function StoryViewer({
  allUsersStories,
  initialUserIndex,
  initialStoryIndex,
  currentUserId,
  token,
  onClose
}) {
  const [currentUserIndex, setCurrentUserIndex] = useState(initialUserIndex);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(initialStoryIndex);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [storyReactions, setStoryReactions] = useState([]);
  const [storyReplies, setStoryReplies] = useState([]);
  const [viewerTab, setViewerTab] = useState("views");
  const [showOptions, setShowOptions] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showRepostModal, setShowRepostModal] = useState(false);
  const [repostPrivacy, setRepostPrivacy] = useState("friends");
  const [isReposting, setIsReposting] = useState(false);
  const [repostSuccess, setRepostSuccess] = useState(false);

  const progressInterval = useRef(null);
  const videoRef = useRef(null);
  const storyDuration = 5000; // 5 seconds per story

  const currentUserStories = allUsersStories[currentUserIndex];
  const stories = currentUserStories?.stories || [];
  const currentStory = stories[currentStoryIndex];
  const storyUser = currentUserStories?.user;
  const isOwnStory = storyUser?._id === currentUserId;

  // Mark story as viewed
  useEffect(() => {
    if (currentStory && !isOwnStory) {
      viewStory(currentStory._id, token);
    }
  }, [currentStory?._id, isOwnStory, token]);

  // Progress bar timer
  useEffect(() => {
    if (isPaused || !currentStory) return;

    setProgress(0);
    const increment = 100 / (storyDuration / 50);

    progressInterval.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          goToNext();
          return 0;
        }
        return prev + increment;
      });
    }, 50);

    return () => clearInterval(progressInterval.current);
  }, [currentStoryIndex, currentUserIndex, isPaused]);

  // Handle video stories
  useEffect(() => {
    if (currentStory?.type === "video" && videoRef.current) {
      if (isPaused) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      videoRef.current.muted = isMuted;
    }
  }, [isPaused, isMuted, currentStory?.type]);

  const goToNext = useCallback(() => {
    if (isTransitioning) return;

    if (currentStoryIndex < stories.length - 1) {
      // Next story from same user
      setCurrentStoryIndex(currentStoryIndex + 1);
    } else if (currentUserIndex < allUsersStories.length - 1) {
      // Next user's stories
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentUserIndex(currentUserIndex + 1);
        setCurrentStoryIndex(0);
        setIsTransitioning(false);
      }, 300);
    } else {
      // End of all stories
      onClose();
    }
  }, [currentStoryIndex, currentUserIndex, stories.length, allUsersStories.length, isTransitioning, onClose]);

  const goToPrev = useCallback(() => {
    if (isTransitioning) return;

    if (currentStoryIndex > 0) {
      // Previous story from same user
      setCurrentStoryIndex(currentStoryIndex - 1);
    } else if (currentUserIndex > 0) {
      // Previous user's last story
      setIsTransitioning(true);
      setTimeout(() => {
        const prevUserStories = allUsersStories[currentUserIndex - 1].stories;
        setCurrentUserIndex(currentUserIndex - 1);
        setCurrentStoryIndex(prevUserStories.length - 1);
        setIsTransitioning(false);
      }, 300);
    }
  }, [currentStoryIndex, currentUserIndex, allUsersStories, isTransitioning]);

  const goToUser = (direction) => {
    if (isTransitioning) return;

    const newUserIndex = direction === "next"
      ? currentUserIndex + 1
      : currentUserIndex - 1;

    if (newUserIndex >= 0 && newUserIndex < allUsersStories.length) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentUserIndex(newUserIndex);
        setCurrentStoryIndex(0);
        setIsTransitioning(false);
      }, 300);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowRight":
          goToNext();
          break;
        case "ArrowLeft":
          goToPrev();
          break;
        case "Escape":
          onClose();
          break;
        case " ":
          e.preventDefault();
          setIsPaused(!isPaused);
          break;
        case "m":
          setIsMuted(!isMuted);
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [goToNext, goToPrev, onClose, isPaused, isMuted]);

  // Touch/click navigation
  const handleTouchStart = (e) => {
    setIsPaused(true);
  };

  const handleTouchEnd = (e) => {
    setIsPaused(false);
  };

  const handleStoryClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;

    if (x < width / 3) {
      goToPrev();
    } else if (x > (width * 2) / 3) {
      goToNext();
    }
  };

  // Reactions
  const handleReaction = async (reaction) => {
    await reactToStory(currentStory._id, reaction, token);
    setShowReactions(false);
  };

  // Reply
  const handleReply = async (e) => {
    e.preventDefault();
    if (replyText.trim()) {
      await replyToStory(currentStory._id, replyText, token);
      setReplyText("");
      setShowReplyInput(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (window.confirm("Tem certeza que deseja excluir este story?")) {
      await deleteStory(currentStory._id, token);
      if (stories.length === 1) {
        if (allUsersStories.length === 1) {
          onClose();
        } else {
          goToNext();
        }
      } else {
        goToNext();
      }
    }
    setShowOptions(false);
  };

  // Load viewers, reactions, and replies
  const loadViewers = async () => {
    setIsPaused(true);
    setViewerTab("views");

    // Load all data in parallel
    const [viewersRes, reactionsRes, repliesRes] = await Promise.all([
      getStoryViewers(currentStory._id, token),
      getStoryReactions(currentStory._id, token),
      getStoryReplies(currentStory._id, token)
    ]);

    if (viewersRes.status === "ok") {
      setViewers(viewersRes.data);
    }
    if (reactionsRes.status === "ok") {
      setStoryReactions(reactionsRes.data);
    }
    if (repliesRes.status === "ok") {
      setStoryReplies(repliesRes.data);
    }

    setShowViewers(true);
  };

  // Repost story
  const handleRepost = async () => {
    if (isReposting) return;
    setIsReposting(true);

    const res = await repostStory(currentStory._id, repostPrivacy, token);

    if (res.status === "ok") {
      setRepostSuccess(true);
      setTimeout(() => {
        setShowRepostModal(false);
        setRepostSuccess(false);
        setIsReposting(false);
      }, 1500);
    } else {
      alert(res.message || "Erro ao republicar story");
      setIsReposting(false);
    }
  };

  // Open repost modal
  const openRepostModal = () => {
    setIsPaused(true);
    setShowRepostModal(true);
  };

  if (!currentStory || !storyUser) return null;

  const renderStoryContent = () => {
    switch (currentStory.type) {
      case "image":
        return (
          <img
            src={currentStory.media}
            alt=""
            className="story_viewer_media"
            draggable={false}
          />
        );
      case "video":
        return (
          <video
            ref={videoRef}
            src={currentStory.media}
            className="story_viewer_media"
            autoPlay
            playsInline
            loop={false}
            muted={isMuted}
          />
        );
      case "text":
        return (
          <div
            className="story_viewer_text_content"
            style={{ background: currentStory.background || "linear-gradient(to bottom right, #1877f2, #42b72a)" }}
          >
            <p>{currentStory.text}</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="story_viewer_overlay" onClick={onClose}>
      <div className="story_viewer_container">
        {/* Navigation arrow - Previous User */}
        {currentUserIndex > 0 && (
          <button
            className="story_nav_arrow story_nav_prev_user"
            onClick={(e) => { e.stopPropagation(); goToUser("prev"); }}
          >
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
            </svg>
          </button>
        )}

        {/* Main Story Card */}
        <div
          className={`story_viewer_card ${isTransitioning ? "transitioning" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Progress bars */}
          <div className="story_progress_wrapper">
            {stories.map((_, index) => (
              <div key={index} className="story_progress_track">
                <div
                  className="story_progress_bar"
                  style={{
                    width: index < currentStoryIndex ? "100%" :
                           index === currentStoryIndex ? `${progress}%` : "0%",
                    transition: index === currentStoryIndex ? "width 0.05s linear" : "none"
                  }}
                />
              </div>
            ))}
          </div>

          {/* Header */}
          <div className="story_viewer_header">
            <Link
              to={`/profile/${storyUser.username}`}
              className="story_viewer_user_info"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="story_viewer_avatar">
                <img src={storyUser.picture} alt="" />
              </div>
              <div className="story_viewer_user_details">
                <span className="story_viewer_name">
                  {storyUser.first_name} {storyUser.last_name}
                </span>
                <span className="story_viewer_time">
                  <Moment fromNow>{currentStory.createdAt}</Moment>
                </span>
              </div>
            </Link>

            <div className="story_viewer_controls">
              {currentStory.type === "video" && (
                <button
                  className="story_control_btn"
                  onClick={() => setIsMuted(!isMuted)}
                >
                  {isMuted ? (
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                    </svg>
                  )}
                </button>
              )}
              <button
                className="story_control_btn"
                onClick={() => setIsPaused(!isPaused)}
              >
                {isPaused ? (
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d="M8 5v14l11-7z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                  </svg>
                )}
              </button>
              <button
                className="story_control_btn"
                onClick={() => setShowOptions(!showOptions)}
              >
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="currentColor" d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                </svg>
              </button>
              <button className="story_control_btn story_close_btn" onClick={onClose}>
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>

            {/* Options dropdown */}
            {showOptions && (
              <div className="story_options_menu" onClick={(e) => e.stopPropagation()}>
                {isOwnStory && (
                  <button onClick={handleDelete} className="story_option_item danger">
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                    </svg>
                    Excluir story
                  </button>
                )}
                <button onClick={() => { setShowOptions(false); }} className="story_option_item">
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                  </svg>
                  Detalhes
                </button>
              </div>
            )}
          </div>

          {/* Story Content */}
          <div
            className="story_viewer_content"
            onClick={handleStoryClick}
            onMouseDown={handleTouchStart}
            onMouseUp={handleTouchEnd}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {renderStoryContent()}

            {/* Text overlay for media stories */}
            {currentStory.text && currentStory.type !== "text" && (
              <div className="story_text_overlay">
                <span>{currentStory.text}</span>
              </div>
            )}
          </div>

          {/* Repost indicator */}
          {currentStory.isRepost && currentStory.originalUser && (
            <div className="story_repost_indicator">
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path fill="currentColor" d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
              </svg>
              <span>
                Republicado de{" "}
                <Link
                  to={`/profile/${currentStory.originalUser.username}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {currentStory.originalUser.first_name}
                </Link>
              </span>
            </div>
          )}

          {/* Footer */}
          <div className="story_viewer_footer">
            {isOwnStory ? (
              <div className="story_owner_footer">
                <div className="story_viewers_section" onClick={loadViewers}>
                  <div className="story_viewers_icon">
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                    </svg>
                  </div>
                  <span>{currentStory.views?.length || 0}</span>
                </div>
                {/* Reactions count for owner */}
                {currentStory.reactions?.length > 0 && (
                  <div className="story_reactions_count" onClick={loadViewers}>
                    <span className="reactions_emojis">
                      {[...new Set(currentStory.reactions.map(r => getReactionEmoji(r.reaction)))].slice(0, 3).join("")}
                    </span>
                    <span>{currentStory.reactions.length}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="story_reply_section">
                {showReplyInput ? (
                  <form onSubmit={handleReply} className="story_reply_form">
                    <input
                      type="text"
                      placeholder="Enviar mensagem..."
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      autoFocus
                      onFocus={() => setIsPaused(true)}
                      onBlur={() => !replyText && setIsPaused(false)}
                    />
                    <button type="submit" disabled={!replyText.trim()}>
                      <svg viewBox="0 0 24 24" width="20" height="20">
                        <path fill="currentColor" d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                      </svg>
                    </button>
                  </form>
                ) : (
                  <div className="story_reply_trigger" onClick={() => setShowReplyInput(true)}>
                    <input type="text" placeholder="Responder..." readOnly />
                  </div>
                )}

                {/* Repost button */}
                <button
                  className="story_repost_trigger"
                  onClick={openRepostModal}
                  title="Republicar"
                >
                  <svg viewBox="0 0 24 24" width="22" height="22">
                    <path fill="currentColor" d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
                  </svg>
                </button>

                <button
                  className="story_react_trigger"
                  onClick={() => setShowReactions(!showReactions)}
                >
                  ❤️
                </button>
              </div>
            )}

            {/* Reactions popup */}
            {showReactions && (
              <div className="story_reactions_bar">
                {reactions.map((r) => (
                  <button
                    key={r.name}
                    className="story_reaction_item"
                    onClick={() => handleReaction(r.name)}
                  >
                    {r.emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Navigation arrow - Next User */}
        {currentUserIndex < allUsersStories.length - 1 && (
          <button
            className="story_nav_arrow story_nav_next_user"
            onClick={(e) => { e.stopPropagation(); goToUser("next"); }}
          >
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
            </svg>
          </button>
        )}

        {/* Preview cards on sides */}
        {currentUserIndex > 0 && (
          <div
            className="story_preview_card story_preview_left"
            onClick={(e) => { e.stopPropagation(); goToUser("prev"); }}
          >
            <div className="story_preview_content">
              <img
                src={allUsersStories[currentUserIndex - 1].user.picture}
                alt=""
                className="story_preview_bg"
              />
              <div className="story_preview_user">
                <img src={allUsersStories[currentUserIndex - 1].user.picture} alt="" />
              </div>
            </div>
          </div>
        )}

        {currentUserIndex < allUsersStories.length - 1 && (
          <div
            className="story_preview_card story_preview_right"
            onClick={(e) => { e.stopPropagation(); goToUser("next"); }}
          >
            <div className="story_preview_content">
              <img
                src={allUsersStories[currentUserIndex + 1].user.picture}
                alt=""
                className="story_preview_bg"
              />
              <div className="story_preview_user">
                <img src={allUsersStories[currentUserIndex + 1].user.picture} alt="" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Viewers/Reactions/Replies Modal */}
      {showViewers && (
        <div className="story_viewers_modal" onClick={() => { setShowViewers(false); setIsPaused(false); }}>
          <div className="story_viewers_content" onClick={(e) => e.stopPropagation()}>
            <div className="story_viewers_header">
              <h3>Detalhes do Story</h3>
              <button onClick={() => { setShowViewers(false); setIsPaused(false); }}>
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="story_viewers_tabs">
              <button
                className={`viewer_tab ${viewerTab === "views" ? "active" : ""}`}
                onClick={() => setViewerTab("views")}
              >
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path fill="currentColor" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                </svg>
                <span>{viewers.length}</span>
              </button>
              <button
                className={`viewer_tab ${viewerTab === "reactions" ? "active" : ""}`}
                onClick={() => setViewerTab("reactions")}
              >
                <span className="tab_emoji">❤️</span>
                <span>{storyReactions.length}</span>
              </button>
              <button
                className={`viewer_tab ${viewerTab === "replies" ? "active" : ""}`}
                onClick={() => setViewerTab("replies")}
              >
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path fill="currentColor" d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18z"/>
                </svg>
                <span>{storyReplies.length}</span>
              </button>
            </div>

            <div className="story_viewers_list">
              {/* Views Tab */}
              {viewerTab === "views" && (
                <>
                  {viewers.length > 0 ? (
                    viewers.map((v) => (
                      <Link
                        key={v.user._id}
                        to={`/profile/${v.user.username}`}
                        className="story_viewer_item"
                      >
                        <img src={v.user.picture} alt="" />
                        <div className="story_viewer_item_info">
                          <span className="story_viewer_item_name">
                            {v.user.first_name} {v.user.last_name}
                          </span>
                          <span className="story_viewer_item_time">
                            <Moment fromNow>{v.viewedAt}</Moment>
                          </span>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="story_no_viewers">
                      <svg viewBox="0 0 24 24" width="48" height="48">
                        <path fill="#65676b" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      </svg>
                      <p>Nenhuma visualização ainda</p>
                    </div>
                  )}
                </>
              )}

              {/* Reactions Tab */}
              {viewerTab === "reactions" && (
                <>
                  {storyReactions.length > 0 ? (
                    storyReactions.map((r) => (
                      <Link
                        key={r.user._id}
                        to={`/profile/${r.user.username}`}
                        className="story_viewer_item"
                      >
                        <img src={r.user.picture} alt="" />
                        <div className="story_viewer_item_info">
                          <span className="story_viewer_item_name">
                            {r.user.first_name} {r.user.last_name}
                          </span>
                          <span className="story_viewer_item_time">
                            <Moment fromNow>{r.reactedAt}</Moment>
                          </span>
                        </div>
                        <span className="story_reaction_emoji">
                          {getReactionEmoji(r.reaction)}
                        </span>
                      </Link>
                    ))
                  ) : (
                    <div className="story_no_viewers">
                      <span style={{ fontSize: "48px" }}>😊</span>
                      <p>Nenhuma reação ainda</p>
                    </div>
                  )}
                </>
              )}

              {/* Replies Tab */}
              {viewerTab === "replies" && (
                <>
                  {storyReplies.length > 0 ? (
                    storyReplies.map((r, i) => (
                      <div key={i} className="story_reply_item">
                        <Link
                          to={`/profile/${r.user.username}`}
                          className="reply_user_info"
                        >
                          <img src={r.user.picture} alt="" />
                          <div className="reply_user_details">
                            <span className="reply_user_name">
                              {r.user.first_name} {r.user.last_name}
                            </span>
                            <span className="reply_time">
                              <Moment fromNow>{r.repliedAt}</Moment>
                            </span>
                          </div>
                        </Link>
                        <div className="reply_message">
                          {r.message}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="story_no_viewers">
                      <svg viewBox="0 0 24 24" width="48" height="48">
                        <path fill="#65676b" d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18z"/>
                      </svg>
                      <p>Nenhuma resposta ainda</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Repost Modal */}
      {showRepostModal && (
        <div className="story_repost_modal" onClick={() => { setShowRepostModal(false); setIsPaused(false); }}>
          <div className="story_repost_content" onClick={(e) => e.stopPropagation()}>
            {repostSuccess ? (
              <div className="repost_success">
                <div className="success_icon">
                  <svg viewBox="0 0 24 24" width="48" height="48">
                    <path fill="#31a24c" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                <h3>Story republicado!</h3>
              </div>
            ) : (
              <>
                <div className="repost_header">
                  <h3>Republicar Story</h3>
                  <button onClick={() => { setShowRepostModal(false); setIsPaused(false); }}>
                    <svg viewBox="0 0 24 24" width="24" height="24">
                      <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>

                <div className="repost_preview">
                  <div className="repost_original_user">
                    <img src={storyUser.picture} alt="" />
                    <span>Story de {storyUser.first_name} {storyUser.last_name}</span>
                  </div>
                  <div className="repost_thumbnail">
                    {currentStory.type === "image" && (
                      <img src={currentStory.media} alt="" />
                    )}
                    {currentStory.type === "video" && (
                      <video src={currentStory.media} />
                    )}
                    {currentStory.type === "text" && (
                      <div
                        className="repost_text_preview"
                        style={{ background: currentStory.background }}
                      >
                        <p>{currentStory.text}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="repost_privacy">
                  <label>Quem pode ver:</label>
                  <select
                    value={repostPrivacy}
                    onChange={(e) => setRepostPrivacy(e.target.value)}
                  >
                    <option value="everyone">Todos</option>
                    <option value="friends">Amigos</option>
                    <option value="close_friends">Amigos próximos</option>
                  </select>
                </div>

                <button
                  className="repost_confirm_btn"
                  onClick={handleRepost}
                  disabled={isReposting}
                >
                  {isReposting ? (
                    "Republicando..."
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" width="20" height="20">
                        <path fill="currentColor" d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
                      </svg>
                      Republicar
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
